import { useEffect, useRef, useState } from "react";
import { Paperclip, Mic, Send, X, BarChart3, MapPin, Image as ImageIcon, Smile, FileText, Square } from "lucide-react";
import type { Message, MessageKind, MediaInfo, Geo } from "@glass/shared";
import { api } from "../lib/api";
import { Modal } from "./Modal";

interface ComposerProps {
  onSend: (kind: MessageKind, body: string, media?: MediaInfo, extra?: { poll?: { question: string; options: string[] }; geo?: Geo }) => void;
  replyTo: Message | null;
  cancelReply: () => void;
  editing: Message | null;
  saveEdit: (newBody: string) => void;
  cancelEdit: () => void;
  sendSoundEnabled: boolean;
}

export function Composer({ onSend, replyTo, cancelReply, editing, saveEdit, cancelEdit }: ComposerProps) {
  const [text, setText] = useState("");
  const [pickerKind, setPickerKind] = useState<"photo" | "video" | "document" | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<{ file: File; url: string; kind: MessageKind } | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [pollOpen, setPollOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recChunks = useRef<BlobPart[]>([]);

  useEffect(() => {
    if (editing) setText(editing.body);
  }, [editing]);

  function pick(kind: "photo" | "video" | "document") {
    setPickerKind(kind);
    setTimeout(() => fileRef.current?.click(), 0);
  }
  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    setPendingFile({ file: f, url, kind: pickerKind === "video" ? "video" : pickerKind === "document" ? "document" : "photo" });
    e.target.value = "";
  }
  async function sendPending() {
    if (!pendingFile) return;
    const up = await api.uploadMedia(pendingFile.file, pendingFile.file.type || "application/octet-stream");
    onSend(pendingFile.kind, text, {
      url: up.url,
      kind: pendingFile.kind,
      mime: up.content_type,
      size: up.size,
      name: pendingFile.file.name,
    });
    setText("");
    URL.revokeObjectURL(pendingFile.url);
    setPendingFile(null);
  }

  async function startRecord() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recChunks.current = [];
      r.ondataavailable = (e) => { if (e.data.size > 0) recChunks.current.push(e.data); };
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recChunks.current, { type: "audio/webm" });
        const up = await api.uploadMedia(blob, "audio/webm");
        onSend("voice", text, { url: up.url, kind: "voice", mime: "audio/webm", size: blob.size });
        setText("");
      };
      r.start();
      recorderRef.current = r;
      setRecording(true);
    } catch (e) {
      alert("Не удалось получить доступ к микрофону: " + (e instanceof Error ? e.message : String(e)));
    }
  }
  function stopRecord() {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  }

  function shareGeo() {
    navigator.geolocation.getCurrentPosition((pos) => {
      onSend("geo", text, undefined, { geo: { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy_m: pos.coords.accuracy } });
      setText("");
    }, (err) => alert("Гео: " + err.message), { enableHighAccuracy: true, maximumAge: 30_000, timeout: 8000 });
  }

  return (
    <div className="px-3 pb-3 pt-2 safe-bottom">
      {(replyTo || editing) && (
        <div className="mb-2 glass-soft px-3 py-2 flex items-center gap-2">
          <div className="flex-1 text-sm">
            <div className="text-xs text-accent-200">{editing ? "Редактирование" : "Ответ"}</div>
            <div className="truncate">{(editing?.body || replyTo?.body || "").slice(0, 120)}</div>
          </div>
          <button className="btn btn-ghost p-1.5 rounded-full" onClick={editing ? cancelEdit : cancelReply}><X size={14} /></button>
        </div>
      )}

      {pendingFile && (
        <div className="mb-2 glass-soft p-3 space-y-2">
          {pendingFile.kind === "photo" && <img src={pendingFile.url} className="max-h-64 rounded-xl mx-auto" alt="preview" />}
          {pendingFile.kind === "video" && <video src={pendingFile.url} className="max-h-64 rounded-xl mx-auto" controls />}
          {pendingFile.kind === "document" && <div className="flex items-center gap-2"><FileText size={18} /> {pendingFile.file.name}</div>}
          <input className="glass-input w-full" placeholder="Подпись (опционально)" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex justify-end gap-2">
            <button className="btn btn-ghost" onClick={() => { URL.revokeObjectURL(pendingFile.url); setPendingFile(null); }}>Отмена</button>
            <button className="btn btn-primary" onClick={sendPending}><Send size={14} /> Отправить</button>
          </div>
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="relative">
          <button className="btn btn-ghost p-2.5 rounded-full" onClick={() => pick("photo")} title="Фото"><ImageIcon size={18} /></button>
        </div>
        <button className="btn btn-ghost p-2.5 rounded-full" onClick={() => pick("document")} title="Файл"><Paperclip size={18} /></button>
        <button className="btn btn-ghost p-2.5 rounded-full" onClick={() => setPollOpen(true)} title="Опрос"><BarChart3 size={18} /></button>
        <button className="btn btn-ghost p-2.5 rounded-full" onClick={shareGeo} title="Геопозиция"><MapPin size={18} /></button>
        <input ref={fileRef} type="file" className="hidden" onChange={onFileChange}
          accept={pickerKind === "photo" ? "image/*" : pickerKind === "video" ? "video/*" : "*/*"} />
        <div className="flex-1 relative">
          <textarea className="glass-input w-full !rounded-2xl resize-none min-h-[44px] max-h-44 pr-10"
            placeholder={editing ? "Изменить сообщение…" : "Сообщение"} value={text} rows={1}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (editing && text.trim()) saveEdit(text.trim());
                else if (text.trim()) { onSend("text", text.trim()); setText(""); }
              }
            }}
          />
          <button className="absolute right-2 bottom-2 btn btn-ghost p-1.5 rounded-full" onClick={() => setShowEmoji((v) => !v)} title="Эмодзи"><Smile size={16} /></button>
          {showEmoji && (
            <div className="absolute right-0 bottom-12 glass-strong p-2 grid grid-cols-8 gap-1 text-xl rounded-2xl">
              {["😀","😁","😂","🤣","😊","😍","😎","🤔","👍","🙏","🎉","🔥","💯","❤️","😢","😡","🥳","🤝","👏","🫶","😴","🙌","💀","✨"].map((e) => (
                <button key={e} className="hover:bg-white/10 rounded-md p-1" onClick={() => { setText((t) => t + e); setShowEmoji(false); }}>{e}</button>
              ))}
            </div>
          )}
        </div>
        {recording ? (
          <button className="btn btn-danger p-2.5 rounded-full animate-pulse-soft" onClick={stopRecord} title="Остановить запись"><Square size={18} /></button>
        ) : text.trim() || editing ? (
          <button className="btn btn-primary p-2.5 rounded-full" onClick={() => {
            if (editing && text.trim()) saveEdit(text.trim());
            else if (text.trim()) { onSend("text", text.trim()); setText(""); }
          }} title="Отправить"><Send size={18} /></button>
        ) : (
          <button className="btn btn-primary p-2.5 rounded-full" onClick={startRecord} title="Голосовое"><Mic size={18} /></button>
        )}
      </div>

      <PollModal open={pollOpen} onClose={() => setPollOpen(false)} onCreate={(q, opts) => {
        onSend("poll", "", undefined, { poll: { question: q, options: opts } });
        setPollOpen(false);
      }} />
    </div>
  );
}

function PollModal({ open, onClose, onCreate }: { open: boolean; onClose: () => void; onCreate: (q: string, opts: string[]) => void }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState<string[]>(["", ""]);
  return (
    <Modal open={open} onClose={onClose} title="Новый опрос">
      <div className="space-y-3">
        <input className="glass-input w-full" placeholder="Вопрос" value={q} onChange={(e) => setQ(e.target.value)} />
        {opts.map((o, i) => (
          <input key={i} className="glass-input w-full" placeholder={`Вариант ${i + 1}`} value={o} onChange={(e) => setOpts(opts.map((v, idx) => (idx === i ? e.target.value : v)))} />
        ))}
        <div className="flex justify-between">
          <button className="btn btn-ghost" onClick={() => setOpts([...opts, ""])} disabled={opts.length >= 10}>+ вариант</button>
          <div className="flex gap-2">
            <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button className="btn btn-primary" disabled={!q.trim() || opts.filter((o) => o.trim()).length < 2} onClick={() => {
              onCreate(q.trim(), opts.map((o) => o.trim()).filter(Boolean));
              setQ(""); setOpts(["", ""]);
            }}>Создать</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
